import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Listen for cart updates to re-attach event listeners
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleCartUpdate = () => {
    // Cart was updated - no need to re-attach listeners with event delegation
    // The event delegation approach handles DOM replacement automatically
    console.log('Cart updated - toggle functionality remains active via event delegation');
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    // Setup show/hide toggle functionality for cart summary
    this.setupShowHideToggle();
  }

  setupShowHideToggle() {
    // Use event delegation on the cart drawer itself
    // This will work even when DOM elements are replaced
    if (!this._toggleListenerAttached) {
      this.addEventListener('click', this.#handleToggleClick.bind(this));
      this._toggleListenerAttached = true;
    }
  }

  /**
   * @param {Event} event - The click event
   */
  #handleToggleClick(event) {
    // Check if the clicked element is the show/hide button
    const target = /** @type {Element} */ (event.target);
    if (target && target.classList && target.classList.contains('cart-show-hide-totals')) {
      this.toggleOriginalTotalContainer();
    }
  }

  toggleOriginalTotalContainer() {
    const container = document.querySelector('.cart__original-total-container');
    const button = document.querySelector('.cart-show-hide-totals');
    
    if (container && button) {
      const isHidden = container.classList.contains('show');
      
      if (isHidden) {
        // Hide the container
        container.classList.remove('show');
        button.textContent = 'Show Details';
      } else {
        // Show the container
        container.classList.add('show');
        button.textContent = 'Hide Details';
      }
    }
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
